import {
  HHAlert,
  HHStack,
  HHTabs,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validate as isValidUUID } from 'uuid';
import UserAdminFeeBills from '../components/bills/user-admin-fee-bills';
import UserEngagements from '../components/engagement/user-engagements';
import UserFeeForService from '../components/fee-for-service/user-fee-for-service';
import UserSubscriptions from '../components/subscriptions/user-subscriptions';
import UserSearch from '../components/user/user-search';
import LoadingComponent from '../components/utils/loading';
import { routes } from '../constants/strings/routes';
import { useBillingSetting } from '../context/billing.context';
import { DynamicDrawerProvider } from '../context/dynamic-drawer.context';
import { useGetUserUuidByIdLazyQuery } from '../types';

const SuccessLoadContent = ({
  userId,
  userUuid,
}: {
  userId: number | null;
  userUuid: string;
}): JSX.Element => (
  <HHTabs
    {...({ layoutStyles: { overflow: 'auto' } } as Record<string, unknown>)}
    variant="fullWidth"
    tabsData={[
      {
        tabContent: <UserSubscriptions userUuid={userUuid} />,
        tabLabel: 'Subscriptions',
        to: `${routes.billing.home}/${routes.billing.userPath}/${userId ?? userUuid}/subscriptions`,
      },
      {
        tabContent: <UserFeeForService userUuid={userUuid} />,
        tabLabel: 'Fee for Service',
        to: `${routes.billing.home}/${routes.billing.userPath}/${userId ?? userUuid}/fee-for-service`,
      },
      {
        tabContent: <UserAdminFeeBills userUuid={userUuid} />,
        tabLabel: 'Admin Fee Bills',
        to: `${routes.billing.home}/${routes.billing.userPath}/${userId ?? userUuid}/admin-fee-bills`,
      },
      {
        tabContent: <UserEngagements userUuid={userUuid} />,
        tabLabel: 'Engagements',
        to: `${routes.billing.home}/${routes.billing.userPath}/${userId ?? userUuid}/engagements`,
      },
    ]}
  />
);

const UserIdPageSkeleton = ({
  children,
  userId,
  userUuid,
}: PropsWithChildren<{
  userId?: number | null;
  userUuid?: string | null;
}>): JSX.Element => {
  const { value, setBillingAppSetting } = useBillingSetting(
    'displayDateTimeLocal',
  );

  return (
    <DynamicDrawerProvider>
      <HHStack flex={1} spacing={4} direction="column" padding={6}>
        <HHStack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <HHStack direction="row" spacing={2} alignItems="center">
            <HHTypography hhVariant="section-title">
              User billing data
            </HHTypography>
            {(userId || userUuid) && (
              <HHTypography hhVariant="subtitle2" color="text.secondary">
                ID {userId ?? userUuid}
              </HHTypography>
            )}
          </HHStack>
          <HHToggleButtonGroup size="small">
            <HHToggleButton
              hhVariant="variant-bypass"
              selected={!value}
              value="UTC"
              onClick={(): void => {
                setBillingAppSetting(false);
              }}
              sx={{ borderRadius: '4px 0 0 4px' }}
            >
              UTC
            </HHToggleButton>
            <HHToggleButton
              hhVariant="variant-bypass"
              selected={value}
              value="Local"
              onClick={(): void => {
                setBillingAppSetting(true);
              }}
              sx={{ borderRadius: '0 4px 4px 0' }}
            >
              Local
            </HHToggleButton>
          </HHToggleButtonGroup>
        </HHStack>
        {children}
      </HHStack>
    </DynamicDrawerProvider>
  );
};

const UserByIdPage = (): JSX.Element => {
  const params = useParams<{ id: string }>();
  const idParam = params.id || '';
  const userId = isValidUUID(idParam) ? null : parseInt(idParam, 10);
  const [userUuid, setUserUuid] = useState<string | null>(
    isValidUUID(idParam) ? idParam : null,
  );
  const [getUserUuidById, { loading, error }] = useGetUserUuidByIdLazyQuery();

  useEffect(() => {
    if (userId) {
      getUserUuidById({ variables: { id: userId } }).then(({ data }) => {
        if (data?.getUserUuidById?.uuid) {
          setUserUuid(data.getUserUuidById.uuid);
        }
      });
    }
  }, [userId, getUserUuidById, setUserUuid]);

  if (loading) {
    return (
      <UserIdPageSkeleton userId={userId} userUuid={userUuid}>
        <LoadingComponent center />;
      </UserIdPageSkeleton>
    );
  }

  if ((!loading && !userUuid) || error) {
    return (
      <UserIdPageSkeleton userId={userId} userUuid={userUuid}>
        <HHAlert hhVariant="outlined" severity="error">
          User with id:{idParam} not found in the system. Please try again.
        </HHAlert>
        <UserSearch />
      </UserIdPageSkeleton>
    );
  }

  return (
    <UserIdPageSkeleton userId={userId} userUuid={userUuid}>
      <SuccessLoadContent userId={userId} userUuid={userUuid as string} />
    </UserIdPageSkeleton>
  );
};

export default UserByIdPage;
